/**
 * @puffin-core/auth-client — security operations (pos superset).
 *
 * `getSecurityOverview` is wrapped in the injected in-flight deduper when
 * present (two hooks call it on mount — DEV-DUP-001). Both are throw-style.
 *
 * 🗑️ TENANT-ID-ORPHAN-REPAIR-001 S6b (owner ruling 2026-10-10, "you should prune"): `changePassword` (pos's
 * `/password/change`) was pruned. Every app changes a password through the ONE route on its self-profile router,
 * through `@puffin-core/page-shell` `useSelfProfile().changePassword`.
 *
 * @module @puffin-core/auth-client/operations/security
 */

import { extractData, authError, networkError } from '../internal.js';

/**
 * @param {Object} ctx
 * @returns {Object} security methods
 */
export function buildSecurityOperations(ctx) {
  const { request, endpoints, mustHaveToken, deduper } = ctx;

  async function fetchSecurityOverview() {
    mustHaveToken();
    const res = await request({
      method: 'GET',
      path: endpoints.getSecurityOverview,
      auth: 'storage',
    });
    if (res.networkError) {
      throw networkError('Unable to load security settings. Please check your connection.');
    }
    if (!res.ok) throw authError(res, 'Failed to fetch security overview');
    return extractData(res.body);
  }

  async function getSecurityOverview() {
    if (deduper) {
      return deduper.run('default', fetchSecurityOverview);
    }
    return fetchSecurityOverview();
  }

  async function signOutAllSessions() {
    mustHaveToken();
    const res = await request({
      method: 'POST',
      path: endpoints.signOutAllSessions,
      auth: 'storage',
    });
    if (res.networkError) throw networkError('Failed to sign out sessions. Please try again.');
    if (!res.ok) throw authError(res, 'Failed to sign out sessions');
    return extractData(res.body);
  }

  return { getSecurityOverview, signOutAllSessions };
}
